import { compact, partition } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { audit, catchError, filter, map, mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import { showCommonDataFetchError } from '../actions/common';
import {
    ADD_MOBILE_APP,
    AddMobileApp,
    FETCH_APP_DATA,
    FETCH_APP_INFO,
    FetchAppData,
    FetchAppInfo,
    fillAppData,
    setInitialCreateAppPageStatus,
    showFetchCreateAppError,
    UPDATE_MOBILE_APP,
    updateAppInfo,
    UpdateMobileApp
} from '../actions/mobile-app';
import {
    fetchAppTrackersTemplates,
    FetchAppTrackersTemplates,
    setAppTrackersMobileOsType,
    TrackingParametersEditorActionType,
    updateAppTrackersAppInfo,
    updateAppTrackersErrors,
    UpdateAppTrackersTemplates,
    updateAppTrackersTemplates,
    updateAppTrackersTrackerData
} from '../actions/trackingParametersEditor';
import { ApiMethodExecutingError } from '../service/Errors';
import { mapMobileTrackingApiErrors } from '../service/MobileTrackerDesignerHelper/MobileTrackerDesignerHelper';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { UrlHelpers } from '../service/UrlHelpers';
import { ValidationResultItem } from '../typings/common';
import { AppFromApiData, AppInfo, MobileSysTypes, UrlCheckResponse } from '../typings/mobile-apps';
import { State } from '../typings/state';
import { JavaAppTrackersTemplates, MOBILE_APP_ID } from '../typings/trackingParametersEditor';

const fetchMobileAppStoreInfoEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_APP_INFO).mergeMap(
        (action: FetchAppInfo): Observable<Action> => {
            return Observable.fromPromise(deps.restApiManager.getAppInfo(action.link, action.login))
                .mergeMap(
                    (appInfo: AppInfo): Array<Action> => {
                        return appInfo.errors && appInfo.errors[0].code
                            ? [showFetchCreateAppError(appInfo.errors)]
                            : [updateAppTrackersAppInfo(MOBILE_APP_ID, appInfo), updateAppInfo(appInfo)];
                    }
                )
                .catch((err: ApiMethodExecutingError) => {
                    return of(showCommonDataFetchError(err));
                });
        }
    );
};

const fetchAppTrackersTemplatesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(TrackingParametersEditorActionType.FETCH_APP_TRACKERS_TEMPLATES).mergeMap(
        (action: FetchAppTrackersTemplates): Observable<Action> => {
            return Observable.fromPromise(deps.restApiManager.getTrackerUrlTemplateList(action.login))
                .map(
                    (trackersTemplates: JavaAppTrackersTemplates): UpdateAppTrackersTemplates => {
                        return updateAppTrackersTemplates(MOBILE_APP_ID, trackersTemplates);
                    }
                )
                .catch((err: ApiMethodExecutingError) => {
                    return of(showCommonDataFetchError(err));
                });
        }
    );
};

const createMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(ADD_MOBILE_APP)
        .mergeMap((action: AddMobileApp) => {
            const state = store$.value;
            const tracker = state.trackingParametersState.data[MOBILE_APP_ID].tracker;
            const { trackerId, trackingSystem } = tracker;
            let trackingUrlForValidation = tracker.url;

            // Временно, исправить в DIRECT-81888
            if (trackingSystem !== MobileSysTypes.other && !trackerId) {
                trackingUrlForValidation = '';
            }

            return Observable.fromPromise(deps.restApiManager.urlCheck(trackingUrlForValidation))
                .mergeMap((value: UrlCheckResponse): Observable<Action> | Array<Action> => {
                    if (value.isValid) {
                        return Observable.fromPromise(
                            deps.restApiManager.createMobileApp(
                                action.login,
                                state.mobileAppsCreate.appSettings,
                                tracker
                            )
                        )
                            .mergeMap(({ success, errors }) => {
                                if (success) {
                                    action.extOnSaveCallback
                                        ? action.extOnSaveCallback()
                                        : UrlHelpers.openUrl(UrlHelpers.getMobileAppsListLink(action.login));

                                    return EMPTY;
                                } else {
                                    const [trackingErrors, mobileAppErrors] = partition(errors, error =>
                                        error.path.startsWith('trackers')
                                    );

                                    return [
                                        showFetchCreateAppError(mobileAppErrors),
                                        updateAppTrackersErrors(
                                            MOBILE_APP_ID,
                                            mapMobileTrackingApiErrors(trackingErrors)
                                        )
                                    ];
                                }
                            })
                            .catch((err: ApiMethodExecutingError) => {
                                return of(showCommonDataFetchError(err));
                            });
                    } else {
                        return [
                            updateAppTrackersErrors(MOBILE_APP_ID, [
                                {
                                    text: value.errorMessage,
                                    path: 'trackers[0].url'
                                }
                            ]),
                            setInitialCreateAppPageStatus()
                        ];
                    }
                })
                .catch((err: ValidationResultItem) => {
                    return of(showFetchCreateAppError([err]));
                });
        })
        .catch((err: ValidationResultItem) => {
            return of(showFetchCreateAppError([err]));
        });
};

const updateMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(UPDATE_MOBILE_APP)
        .mergeMap((action: UpdateMobileApp) => {
            const state = store$.value;
            const tracker = state.trackingParametersState.data[MOBILE_APP_ID].tracker;
            const { trackerId, trackingSystem } = tracker;
            let trackingUrlForValidation = tracker.url;

            // Временно, исправить в DIRECT-81888
            if (trackingSystem !== MobileSysTypes.other && !trackerId) {
                trackingUrlForValidation = '';
            }

            return Observable.fromPromise(deps.restApiManager.urlCheck(trackingUrlForValidation))
                .mergeMap((value: UrlCheckResponse): Observable<Action> | Array<Action> => {
                    if (value.isValid) {
                        return Observable.fromPromise(
                            deps.restApiManager.updateMobileApp(
                                action.login,
                                state.mobileAppsCreate.appSettings,
                                tracker
                            )
                        )
                            .mergeMap(({ success, errors }) => {
                                if (success) {
                                    action.extOnSaveCallback
                                        ? action.extOnSaveCallback()
                                        : UrlHelpers.openUrl(UrlHelpers.getMobileAppsListLink(action.login));

                                    return EMPTY;
                                } else {
                                    const [trackingErrors, mobileAppErrors] = partition(errors, error =>
                                        error.path.startsWith('trackers')
                                    );

                                    return [
                                        showFetchCreateAppError(mobileAppErrors),
                                        updateAppTrackersErrors(
                                            MOBILE_APP_ID,
                                            mapMobileTrackingApiErrors(trackingErrors)
                                        )
                                    ];
                                }
                            })
                            .catch((err: ApiMethodExecutingError) => {
                                return of(showCommonDataFetchError(err));
                            });
                    } else {
                        return [
                            updateAppTrackersErrors(MOBILE_APP_ID, [
                                {
                                    text: value.errorMessage,
                                    path: 'trackers[0].url'
                                }
                            ]),
                            setInitialCreateAppPageStatus()
                        ];
                    }
                })
                .catch((err: ValidationResultItem) => {
                    return of(showFetchCreateAppError([err]));
                });
        })
        .catch((err: ValidationResultItem) => {
            return of(showFetchCreateAppError([err]));
        });
};

const fetchMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_APP_DATA),
        audit(() => store$.pipe(filter(state => state.trackingParametersState.trackersTemplates.length !== 0))),
        mergeMap(
            (action: FetchAppData): Observable<Action> => {
                return from(deps.restApiManager.getAppData(action.id, action.login)).pipe(
                    mergeMap(
                        (data: AppFromApiData): Array<Action> => {
                            const osType = data.appInfo.osType;

                            return compact([
                                fillAppData(data),
                                osType && setAppTrackersMobileOsType(MOBILE_APP_ID, osType),
                                updateAppTrackersTrackerData(MOBILE_APP_ID, data.tracker)
                            ]);
                        }
                    ),
                    catchError(
                        (err: ApiMethodExecutingError): Observable<Action> => {
                            return of(showCommonDataFetchError(err));
                        }
                    )
                );
            }
        )
    );
};

const fetchMobileAppTemplatesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_APP_DATA),
        map((action: FetchAppData) => fetchAppTrackersTemplates(MOBILE_APP_ID, action.login))
    );
};

export {
    fetchMobileAppStoreInfoEpic,
    createMobileAppEpic,
    fetchAppTrackersTemplatesEpic,
    fetchMobileAppEpic,
    updateMobileAppEpic,
    fetchMobileAppTemplatesEpic
};
